import React, { useCallback, useState } from 'react';
import Form from 'react-jsonschema-form';
import { Button } from 'reactstrap';
import ReactDOM from 'react-dom';
import axios from 'axios';
import moment from 'moment-timezone';

import MeterPointSelector from '../../../MeterPointSelector/MeterPointSelector';
import VendorSearch from '../VendorSearch/VendorSearch';
import { dataServiceChangeSchema, dataServiceChangeUISchema } from '../../schemas/dataServiceChangeSchema';
import { useProcessForm } from '../../hooks/useProcessForm';
import ExpiringAlert from '../../../ExpiringAlert';

const DataServiceChangeForm = ({ onClose, initialData }) => {
  const initial = initialData && initialData.mpan_core
    ? { ...initialData, mpan_core: { name: initialData.mpan_core } }
    : initialData || {};
  const { formContext, updateFormContext, isLoading, setIsLoading } = useProcessForm(initial);
  const [serviceReference, setServiceReference] = useState(null);
  const [selectedVendor, setSelectedVendor] = useState(null);

  const handleChange = useCallback(
    ({ formData }) => {
      updateFormContext(formData);
    },
    [updateFormContext],
  );

  const transformErrors = useCallback((errors) => {
    return errors.map((error) => {
      switch (error.property) {
        case '.mpan_core':
        case '.mpan_core.name':
          return { ...error, message: 'MPAN Core is a required property' };
        case '.data_service_mpid':
          return { ...error, message: 'Data Service MPID is a required property' };
        case '.effective_from':
          return { ...error, message: 'Effective From is a required property' };
        case '.appointment_scenario':
          return { ...error, message: 'Appointment Scenario is a required property' };
        case '.consent_granularity':
          return { ...error, message: 'Consent Granularity is a required property' };
        default:
          return error;
      }
    });
  }, []);

  const formatDateTime = (dateTimeString) => {
  if (!dateTimeString) return null;

  const hasTimezone = /([zZ]|[+\-]\d{2}:\d{2})$/.test(dateTimeString);

  const parsedDate = hasTimezone
    ? moment.parseZone(dateTimeString)
    : moment.tz(dateTimeString, 'Europe/London');

  if (!parsedDate.isValid()) {
    return null;
  }

  return parsedDate
    .tz('Europe/London')
    .startOf('day')
    .format('YYYY-MM-DDTHH:mm:ssZ');
};

  const transformFormDataToPayload = (formData) => {
    // Get the actual mdmIdentificationNumber for the API payload
    const dataServiceMpid = selectedVendor ? selectedVendor.mdmIdentificationNumber : formData.data_service_mpid;

    const payload = {
      mpan_core: parseInt(formData.mpan_core.name, 10),
      data_service_mpid: dataServiceMpid,
      effective_from: formatDateTime(formData.effective_from),
      appointment_scenario: formData.appointment_scenario,
      consent_granularity: formData.consent_granularity,
      mdr_mpid: null,
      mdr_effective_from: null,
      fall_back_read_frequency: null,
      domestic_premise_indicator: formData.mpan_core.marketSector === 'Domestic',
    };

    // Add contract_reference if provided, otherwise add service reference if available
    if (formData.contract_reference && formData.contract_reference.trim()) {
      payload.contract_reference = formData.contract_reference.trim();
    } else if (serviceReference) {
      payload.contract_reference = serviceReference;
    }

    return payload;
  };

  const uiSchemaWithWidgets = {
    ...dataServiceChangeUISchema,
    mpan_core: {
      ...dataServiceChangeUISchema.mpan_core,
      'ui:field': (props) => (
        <div className='form-group field'>
          <label className='control-label' htmlFor='root_mpan_core'>
            MPAN Core<span className='required'>*</span>
          </label>
          <MeterPointSelector
            value={props.formData}
            onChange={props.onChange}
            placeholder='Select MPAN'
            includeAllStatuses={true}
            includeMarketSector={true}
          />
        </div>
      ),
    },
    data_service_mpid: {
      ...dataServiceChangeUISchema.data_service_mpid,
      'ui:widget': (props) => (
        <VendorSearch
          {...props}
          placeholder='Select Data Service Provider'
          onServiceReferenceUpdate={setServiceReference}
          onVendorSelect={setSelectedVendor}
        />
      ),
    },
  };

  const loadingIcon = isLoading ? (
    <span>
      <i className='fa fa-circle-o-notch fa-spin'></i>{' '}
    </span>
  ) : null;

  return (
    <div className='data-service-change-form'>
      <Form
        schema={dataServiceChangeSchema}
        uiSchema={uiSchemaWithWidgets}
        formContext={formContext}
        formData={formContext}
        transformErrors={transformErrors}
        showErrorList={false}
        onSubmit={({ formData }) => {
          setIsLoading(true);
          const payload = transformFormDataToPayload(formData);
          console.log('Submitting payload to BOP.Dataflows.StartDataServicesChange:', payload);
          axios
            .post('/publishMessage', payload, {
              headers: {
                Authorization: `Bearer ${localStorage.token}`,
              },
              params: { queue: 'BOP.Dataflows.StartDataServicesChange' },
            })
            .then((response) => {
              setIsLoading(false);
              if (response.data === 'Message sent') {
                ReactDOM.render(
                  <ExpiringAlert color='success' message='Data Service Change request submitted successfully!' />,
                  document.getElementById('alert').appendChild(document.createElement('div')),
                );
                onClose();
              } else {
                ReactDOM.render(
                  <ExpiringAlert color='danger' message={response.data.message || 'Error submitting request'} />,
                  document.getElementById('alert').appendChild(document.createElement('div')),
                );
              }
            })
            .catch((error) => {
              setIsLoading(false);
              // eslint-disable-next-line no-console
              console.error('Error submitting data service change request:', error);
              ReactDOM.render(
                <ExpiringAlert color='danger' message='Error submitting request. Request logged to console.' />,
                document.getElementById('alert').appendChild(document.createElement('div')),
              );
            });
        }}
        onChange={handleChange}
      >
        <div>
          <Button type='submit' disabled={isLoading} color='primary'>
            {isLoading && loadingIcon}Submit Request
          </Button>
          &nbsp;&nbsp;&nbsp;
          <Button type='button' onClick={onClose}>
            Cancel
          </Button>
        </div>
      </Form>
    </div>
  );
};

export default DataServiceChangeForm;
